import { clientLogger } from '@/lib/clientLogger';
import { useRef, useEffect, useState, useCallback } from 'react';


interface SignaturePadProps {
  onSignatureComplete: (signaturePath: string, signerName: string) => void | Promise<void>;
  onCancel: () => void;
  width?: number;
  height?: number;
  initialSignaturePath?: string;
  initialSignerName?: string;
}

export function SignaturePad({
  onSignatureComplete,
  onCancel,
  width = 360,
  height = 200
  ,
  initialSignaturePath,
  initialSignerName
}: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signerName, setSignerName] = useState(initialSignerName ?? '');
  // Keep recorded strokes (array of strokes -> array of points)
  const strokesRef = useRef<Array<Array<{ x: number; y: number }>>>([]);
  // If an initial signature path was provided, keep it so we can return it
  const initialSignatureRef = useRef<string | null>(null);
  // Track if we're showing an initial signature (disables drawing and sign button)
  const [showingInitialSignature, setShowingInitialSignature] = useState(false);

  // Initialize and resize canvas responsively
  useEffect(() => {
    try {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) return;

      const dpr = window.devicePixelRatio || 1;

      const setupCanvas = () => {
        try {
          const rect = container.getBoundingClientRect();
          const cssWidth = Math.max(0, Math.floor(rect.width));
          const cssHeight = height;

          // Set the element's CSS size
          canvas.style.width = cssWidth + 'px';
          canvas.style.height = cssHeight + 'px';

          // Set the internal pixel size for high-DPI displays
          canvas.width = cssWidth * dpr;
          canvas.height = cssHeight * dpr;

          const ctx = canvas.getContext('2d');
          if (!ctx) return;

          // Scale so drawing coordinates use CSS pixels
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          ctx.scale(dpr, dpr);

          // Set drawing properties (use CSS pixel units)
          ctx.strokeStyle = '#000000';
          ctx.lineWidth = 2;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';

          // Fill with white background
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, cssWidth, cssHeight);

          // If an initial signature path or image is provided, render it onto the canvas.
          if (initialSignaturePath) {
            initialSignatureRef.current = initialSignaturePath;
            if (initialSignaturePath.startsWith('data:') || initialSignaturePath.startsWith('http') || initialSignaturePath.startsWith('blob:')) {
              const img = new Image();
              img.crossOrigin = 'anonymous';
              img.onload = () => {
                try {
                  ctx.drawImage(img, 0, 0, cssWidth, cssHeight);
                  setHasSignature(true);
                  setShowingInitialSignature(true);
                } catch (err) {
                  clientLogger.warn('Failed to draw signature image', err);
                  // Continue without the initial image
                }
              };
              img.onerror = (err) => {
                clientLogger.warn('Failed to load signature image', err);
                // Continue without the initial image
              };
              img.src = initialSignaturePath;
            } else {
              try {
                const path = new Path2D(initialSignaturePath);
                ctx.stroke(path);
                setHasSignature(true);
                setShowingInitialSignature(true);
              } catch (err) {
                clientLogger.warn('Failed to draw initial signature path', err);
                // Continue without the initial path
              }
            }
          }
        } catch (err) {
          clientLogger.error('Canvas setup failed:', err);
        }
      };

      // Initial setup
      setupCanvas();

      // Observe container size changes to re-setup canvas
      if (typeof ResizeObserver !== 'undefined') {
        const ro = new ResizeObserver(() => {
          try {
            setupCanvas();
          } catch (err) {
            clientLogger.error('Canvas resize failed:', err);
          }
        });
        ro.observe(container);

        return () => {
          try {
            ro.disconnect();
          } catch (err) {
            clientLogger.error('ResizeObserver disconnect failed:', err);
          }
        };
      }
    } catch (err) {
      clientLogger.error('Canvas initialization error:', err);
    }
  }, [height, initialSignaturePath]);

  const startDrawing = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (showingInitialSignature) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    try {
      const element = e.currentTarget as HTMLCanvasElement;
      if (element.setPointerCapture) {
        element.setPointerCapture(e.pointerId);
      }
    } catch (err) {
      // Silently fail on iOS or older browsers
      clientLogger.debug('Pointer capture not supported');
    }
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    strokesRef.current.push([{ x, y }]);
    initialSignatureRef.current = null;
  }, [showingInitialSignature]);

  const draw = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
    const strokes = strokesRef.current;
    if (strokes.length > 0) {
      strokes[strokes.length - 1].push({ x, y });
    }
  }, [isDrawing]);

  const stopDrawing = useCallback((e?: React.PointerEvent<HTMLCanvasElement>) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDrawing(false);
    if (e) {
      try {
        const element = e.currentTarget as HTMLCanvasElement;
        if (element.releasePointerCapture) {
          element.releasePointerCapture(e.pointerId);
        }
      } catch (err) {
        // Silently fail on iOS or older browsers
        clientLogger.debug('Pointer release not supported');
      }
    }
  }, []);

  const clearSignature = () => {
    if (isSubmitting) return;
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const rect = container.getBoundingClientRect();
    const cssWidth = Math.max(0, Math.floor(rect.width));
    const cssHeight = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, cssWidth, cssHeight);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, cssWidth, cssHeight);
    setHasSignature(false);
    strokesRef.current = [];
    initialSignatureRef.current = null;
    setShowingInitialSignature(false);
    setSignerName('');
  };

  const getSignaturePath = (): string => {
    const canvas = canvasRef.current;
    if (!canvas) return '';
    // If there are no recorded strokes but an initial SVG path was provided, return it
    if ((strokesRef.current.length === 0 || strokesRef.current.every(s => s.length === 0)) && initialSignatureRef.current) {
      return initialSignatureRef.current;
    }

    // Build a simple SVG path from recorded strokes. We use straight lines (M + L commands).
    // For smoother curves you could implement bezier smoothing, but this is reliable and simple.
    const parts: string[] = [];
    for (const stroke of strokesRef.current) {
      if (!stroke || stroke.length === 0) continue;
      const first = stroke[0];
      parts.push(`M ${Math.round(first.x)} ${Math.round(first.y)}`);
      for (let i = 1; i < stroke.length; i++) {
        const p = stroke[i];
        parts.push(`L ${Math.round(p.x)} ${Math.round(p.y)}`);
      }
    }

    return parts.join(' ');
  };

  const handleComplete = async () => {
    if (!hasSignature || !signerName.trim() || isSubmitting) return;

    const signaturePath = getSignaturePath();
    setIsSubmitting(true);
    try {
      await Promise.resolve(onSignatureComplete(signaturePath, signerName.trim()));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Please sign below</h3>
        <p className="text-sm text-gray-600">Use your mouse or touch to sign in the box below</p>
      </div>

      {/* Hidden while showing a read-only existing signature we have no name for —
          otherwise an empty "Full name" box sits under a rendered signature. */}
      {(!showingInitialSignature || signerName) && (
        <div>
          <label htmlFor="signer-name" className="block text-sm font-medium text-gray-700 mb-1">
            Full name
          </label>
          <input
            id="signer-name"
            type="text"
            value={signerName}
            onChange={(e) => setSignerName(e.target.value)}
            disabled={showingInitialSignature || isSubmitting}
            readOnly={showingInitialSignature}
            maxLength={200}
            autoComplete="name"
            placeholder="Type your full name"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
          />
        </div>
      )}

      <div ref={containerRef} className="border-2 border-gray-300 rounded-lg p-4 bg-white" style={{overflow: 'hidden'}}>
        <canvas
          ref={canvasRef}
          className="border border-gray-200 cursor-crosshair"
          onPointerDown={startDrawing}
          onPointerMove={draw}
          onPointerUp={stopDrawing}
          style={{ display: 'block', margin: '0 auto', opacity: showingInitialSignature ? 0.5 : 1, pointerEvents: showingInitialSignature ? 'none' : 'auto', width: '100%', maxWidth: `${width}px`, touchAction: 'none' }}
        />
      </div>

      <div className="flex justify-between gap-3">
        <button
          type="button"
          onClick={clearSignature}
          disabled={!hasSignature || isSubmitting}
          className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md font-medium hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Clear
        </button>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-4 py-2 bg-gray-300 text-gray-900 rounded-md font-medium hover:bg-gray-400 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleComplete}
            disabled={!hasSignature || !signerName.trim() || showingInitialSignature || isSubmitting}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting && (
              <svg
                className="h-4 w-4 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            )}
            {isSubmitting ? 'Sending...' : 'Sign Invoice'}
          </button>
        </div>
      </div>
    </div>
  );
}
